/**
 * useWorkOrderLineSave — shared save path for a single work-order line item.
 *
 * Lifted from WorkOrderLineItemForm.tsx's saveLineItem (PUT/POST endpoint
 * selection) + buildPayload's part-cleaning logic, so both the Drawer form
 * and the future inline editor write through one path.
 */

import { useState, useCallback } from 'react';
import api from '../services/api';
import { WorkOrderLineItem, WorkOrderLinePart } from '../types';

export interface SaveLineArgs {
  workOrderId: number;
  lineItem: WorkOrderLineItem; // includes .parts array
  isNew: boolean;
}

function cleanParts(parts: WorkOrderLinePart[] | undefined): any[] {
  return (parts || []).map(part => {
    const cleanPart: any = {
      part_name: part.part_name,
      quantity: part.quantity,
      unit_price: part.unit_price,
      total_price: part.total_price,
      status: part.status || 'needed',
      discount_percentage: part.discount_percentage || 0,
      discount_amount: part.discount_amount || 0,
      cost: part.cost || null,
      markup_preset_id: part.markup_preset_id ?? null,
      markup_percentage: part.markup_percentage ?? null,
    };

    // I-C: owner-only true cost in integer cents (used by PnlService profit math).
    // Only forward when present so non-owner saves never clobber an existing value.
    if (part.cost_cents != null) {
      cleanPart.cost_cents = part.cost_cents;
      if (part.cost_source) cleanPart.cost_source = part.cost_source;
    }

    // Only include optional fields if they have truthy values
    if (part.part_number) cleanPart.part_number = part.part_number;
    if (part.supplier) cleanPart.supplier = part.supplier;
    if (part.tracking_number) cleanPart.tracking_number = part.tracking_number;
    if (part.expected_delivery) cleanPart.expected_delivery = part.expected_delivery;
    if (part.notes) cleanPart.notes = part.notes;
    if (part.discounted_price != null) cleanPart.discounted_price = part.discounted_price;

    return cleanPart;
  });
}

export function useWorkOrderLineSave(): {
  saveLine: (args: SaveLineArgs) => Promise<WorkOrderLineItem>;
  saving: boolean;
} {
  const [saving, setSaving] = useState(false);

  const saveLine = useCallback(async ({ workOrderId, lineItem, isNew }: SaveLineArgs): Promise<WorkOrderLineItem> => {
    setSaving(true);
    try {
      const payload: any = { ...lineItem, parts: cleanParts(lineItem.parts) };

      if (!isNew && lineItem.id) {
        const res = await api.put(`/work-orders/${workOrderId}/line-items/${lineItem.id}`, payload);
        return res.data;
      }

      payload.work_order_id = workOrderId;
      const res = await api.post(`/work-orders/${workOrderId}/line-items`, payload);
      return res.data;
    } finally {
      setSaving(false);
    }
  }, []);

  return { saveLine, saving };
}
